import { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useMatrixClient } from '../../context/MatrixContext';
import { useDisambiguation } from '../../context/DisambiguationContext';
import { useUserAvatar } from '../../hooks/useUserAvatar';
import { generateInitialsAvatar } from '../../utils/avatar';
import './MentionPill.css';

/**
 * Renders a Matrix user mention as a styled inline pill with avatar and display name.
 * Self-mentions (current user) are highlighted with a distinct style.
 *
 * @param {Object} props
 * @param {string} props.userId - Matrix user ID (e.g., "@user:matrix.org")
 * @param {string} [props.displayText] - Display text to show (defaults to display name or userId)
 * @param {Function} [props.onClick] - Callback when pill is clicked, receives userId
 */
const MentionPill = ({ userId, displayText, onClick }) => {
  const client = useMatrixClient();
  const { needsDisambiguation } = useDisambiguation();
  const currentUserId = client?.getUserId?.();
  const [avatarError, setAvatarError] = useState(false);

  // Get user info from Matrix client
  const user = client?.getUser?.(userId);
  const displayName = displayText || user?.displayName || userId;

  // Fetch avatar
  const { avatarUrl } = useUserAvatar(userId, displayName, 16);

  // Generate fallback avatar
  const fallbackAvatar = useMemo(
    () => generateInitialsAvatar(displayName, 16),
    [displayName],
  );

  // Determine if this is a self-mention
  const isSelfMention = userId === currentUserId;

  // Handle avatar load error
  const handleAvatarError = useCallback(() => {
    setAvatarError(true);
  }, []);

  // Determine which avatar to show
  const avatarSrc = avatarError ? fallbackAvatar : (avatarUrl || fallbackAvatar);

  const handleClick = (e) => {
    e.stopPropagation();
    onClick?.(userId);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onClick?.(userId);
    }
  };

  return (
    <span
      className={`mention-pill${isSelfMention ? ' self-mention' : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      title={userId}
      role="button"
      tabIndex={0}
      aria-label={isSelfMention ? `You were mentioned: ${displayName}` : `Mention: ${displayName}`}
    >
      <img
        src={avatarSrc}
        alt=""
        className="mention-pill-avatar"
        onError={handleAvatarError}
      />
      <span className="mention-pill-text">
        {displayName}
        {needsDisambiguation(userId) && (
          <span className="mention-pill-mxid"> ({userId})</span>
        )}
      </span>
    </span>
  );
};

MentionPill.propTypes = {
  userId: PropTypes.string.isRequired,
  displayText: PropTypes.string,
  onClick: PropTypes.func,
};

MentionPill.defaultProps = {
  displayText: null,
  onClick: null,
};

export default MentionPill;
